import React, { useEffect, useMemo } from 'react'
import Module from '../../layout/Module'
import { useDispatch, useSelector } from 'react-redux'
import { FormControl, MenuItem, Select, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { setCurrentCurriculum } from '../../../store/actions/assessment.actions'
import Loading from '../../Loading/Loading'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import isEmpty from 'lodash/isEmpty'
import { updateFilter } from '../../../store/actions/filter.actions'
import { updateSkip } from '../../../store/actions/datatable.actions'
import { toggleAlertModule } from '../../../store/actions/alert.actions'

const useStyles = makeStyles(() => ({
  formControl: {
    width: 150,
    backgroundColor: 'var(--background)',
    borderRadius: '0.25rem',
    padding: '0'
  },
  select: {
    padding: '0.5rem 0.75rem '
  }
}))

const MessageAlert = () => (
  <div className='flex flex-col justify-center items-center h-full'>
    <FontAwesomeIcon
      icon={['fas', 'exclamation-triangle']}
      size='3x'
      className='text-muted-foregroud opacity-25'
    />
    <Typography className='text-muted-foregroud opacity-25'>No curriculum available</Typography>
  </div>
)

const AssessmentModule = ({ children, title, titleLink, actions, moduleName }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const params = useParams()
  const { pathname } = useLocation()

  const classes = useStyles()
  const curriculumId = params['*']?.split('/')[0]
  const redirectStudentId = params.studentId ? `${params.studentId}/` : ''
  const basepathclass = `/dashboard/${redirectStudentId}classes`
  const curriculumList = useSelector((state) => state?.assessment?.curriculum || [])
  const isLoading = useSelector((state) => state?.assessment?.loading)
  const current = useSelector((state) => state?.assessment?.currentCurriculum)
  const alertSave = useSelector((state) => state?.alert?.save || {})
  const permissionsListClass = useSelector((state) => state?.session?.permissions?.class?.list || [])

  const onCurriculumChange = (value) => {
    const onClick = () => {
      dispatch(setCurrentCurriculum(value))
      navigate(`${titleLink}/${value}`)
      if (moduleName !== 'dr') {
        dispatch(updateFilter(moduleName, {}))
      }
      dispatch(updateSkip(moduleName, 0))
    }

    if (alertSave && alertSave.mode) {
      dispatch(
        toggleAlertModule(true, alertSave.submodule, alertSave.mode, alertSave.onSave, () => {
          onClick()
        })
      )
    } else {
      onClick()
    }
  }

  useEffect(() => {
    if (!curriculumId && curriculumList?.length > 0) {
      const ids = ['unassigned', 'assigned', 'all']
      if (current && !ids.includes(current?._id)) {
        onCurriculumChange(current._id)
      } else {
        onCurriculumChange(curriculumList[0]._id)
      }
    } else {
      if (!curriculumId && pathname.includes(basepathclass) && !permissionsListClass.includes('all')) {
        onCurriculumChange('all')
      }
    }
  }, [curriculumId, curriculumList, current])

  useEffect(() => {
    if (!current) {
      dispatch(setCurrentCurriculum(curriculumId))
    }
  }, [curriculumId, current, curriculumList])

  const curriculum = useMemo(() => {
    let list = [...curriculumList]
    if (pathname.includes(basepathclass)) {
      if (permissionsListClass.includes('all')) {
        const find = list.find((item) => item._id === 'unassigned')
        if (!find) {
          list.push({
            _id: 'unassigned',
            shortName: 'N/A',
            name: 'Unassigned'
          })
        }
      } else {
        if (list.length === 0) {
          list = [
            {
              _id: 'all',
              shortName: 'All',
              name: 'ALL'
            }
          ]
        }
      }
    } else {
      list = list.filter((item) => item._id !== 'unassigned')
    }
    return list
  }, [curriculumList, pathname])

  return (
    <Module moduleName={moduleName}>
      <Module.Header title={title} classNameTitle='text-nowrap min-w-230px max-w-230px'>
        <div className='flex justify-between items-center w-full gap-2'>
          <div className=''>
            {curriculum?.length > 1 && (
              <FormControl variant='outlined' className={classes.formControl}>
                <Select
                  variant='outlined'
                  value={curriculumId}
                  onChange={(e) => onCurriculumChange(e.target.value)}
                  classes={{
                    outlined: classes.select
                  }}
                  MenuProps={{
                    className: classes.select,
                    style: {
                      padding: '0.5rem'
                    }
                  }}
                >
                  {curriculum?.map((cur) => (
                    <MenuItem key={`curriculum-${cur._id}`} value={cur._id}>
                      {cur.shortName} Curriculum
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>
          <div className='flex justify-end items-center gap-2'>{actions}</div>
        </div>
      </Module.Header>
      <Module.Body>
        {isLoading ? (
          <Loading />
        ) : curriculum?.length === 0 ? (
          <MessageAlert />
        ) : isEmpty(current) ? (
          <MessageAlert />
        ) : (
          children
        )}
      </Module.Body>
    </Module>
  )
}

export default AssessmentModule
